import React, { useMemo } from 'react';
import { trpc } from 'src/utils/trpc';
import MechanicalTable from './Table';
import { InspectionTemplateData } from '../../../types/inspections/inspections';

type MechanicalTabProps = {
  vehicle_inspection: any;
};

type Payload = {
  payload: { comment: string | null } | null;
};

const MechanicalTab = ({ vehicle_inspection }: MechanicalTabProps) => {
  const getItemsFromTemplate = (template: InspectionTemplateData) => {
    const items: string[] = [];
    template?.categories?.forEach((category) => {
      category?.items?.forEach((item) => {
        items.push(item.id);
      });
    });
    return items;
  };

  const { data: categories } =
    trpc.inspection_template_categories.getAll.useQuery(undefined, {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    });

  const { data: items } = trpc.inspection_template_items.getAll.useQuery(
    {
      inspection_item_ids: getItemsFromTemplate(
        vehicle_inspection?.template as InspectionTemplateData
      ),
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const { data: comments } = trpc.comments.getPublicByTaskIds.useQuery(
    {
      task_ids:
        vehicle_inspection?.vehicle_inspection_items?.map(
          (item: any) => item?.id
        ) || [],
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  const categoryMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    categories?.forEach((category: any) => {
      map[category.id] = category.title;
    });
    return map;
  }, [categories]);

  const itemTemplateMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    items?.forEach((item: any) => {
      map[item.id] = item.description;
    });
    return map;
  }, [items]);

  const parseCommentMentions = ({ payload }: Payload) => {
    const regexpNames = /@\[(.+?)\]\((.+?)\)/g;

    const textComponents = payload?.comment
      ?.split(regexpNames)
      .reduce((acc = '', part = '', i) => {
        /**
         * @param {string} payload.comment ex. @[Steve Jobs](someFakeId) look at this.
         *
         * regexpNames splits the string into sets of 3 parts.
         * 0: msg text (if the first char is @ then it will be an empty string '')
         * 1: name of person to mention (starts with @ and ends with ])
         * 2: id of mentioned person (is enclosed in parens)
         *
         * This pattern will continue for any number of mentions.
         * using mudulo 3 we can determine which position the value is in and act accordingly.
         * 0 or 1 - return it as plain text
         * otherwise just return.
         */
        if (i % 3 === 1 || i % 3 !== 2) acc = `${acc}${part}`;

        return acc;
      }, '');

    return textComponents;
  };

  const itemMap = (category_id: string) => {
    const map = [] as any;
    vehicle_inspection.vehicle_inspection_items?.forEach(
      (item: any, index: number) => {
        const categoryItems =
          vehicle_inspection?.template?.categories?.find(
            (cat: any) => cat?.id === category_id
          )?.items ?? [];
        if (
          categoryItems?.find(
            (ci: any) => ci?.id === item?.inspection_template_item_id
          )
        ) {
          map.push({
            taskId: item.id,
            id: (index += 1),
            name: itemTemplateLookup(item.inspection_template_item_id),
            grey: item.grey,
            green: item.green,
            yellow: item.yellow,
            red: item.red,
            fixed: item.fixed,
            comments:
              comments
                ?.filter((comment) => comment?.key === item.id)
                ?.sort((a, b) => {
                  return (
                    new Date(a.created_at).getTime() -
                    new Date(b.created_at).getTime()
                  );
                })
                ?.map(
                  (comment) =>
                    parseCommentMentions({
                      payload: comment?.payload,
                    } as Payload) ?? ''
                ) ?? [],
          });
        }
      }
    );
    return map;
  };

  const categoryLookup = (id: string) => {
    return categoryMap[id];
  };

  const itemTemplateLookup = (id: string) => {
    return itemTemplateMap[id];
  };

  const itemLookup = (category_id: string) => {
    return itemMap(category_id);
  };

  if (!vehicle_inspection) return <div> No Vehicle Inspections </div>;
  return (
    <>
      {vehicle_inspection.template.categories.map((category: any) => {
        const categoryItems = itemLookup(category.id);
        return (
          <div key={category.id}>
            <MechanicalTable
              title={categoryLookup(category.id)}
              rows={categoryItems}
            />
            <div className="my-4" />
          </div>
        );
      })}
    </>
  );
};

export default MechanicalTab;
